import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Eye, EyeOff, Briefcase, Search, Building2, UserCircle2, ShieldCheck, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { authApi, type LoginWorkspace } from '@/services/auth'
import { buildSignupEmailVerificationPath, isSignupEmailOtpVerificationContext } from '@/lib/auth-verification'
import { orgTreeApi } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'
import { buildPostSelectionPath, consumeAuthRedirectContext, getSafeAuthRedirectTarget } from '@/lib/auth-redirect'
import { getErrorMessage } from '@/lib/api'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { buildServiceVariantHomePath, withServiceInstanceId } from '@/lib/service-instance'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { TenantAuthShell } from '@/components/auth/tenant-auth-shell'
import {
  assertAuthThemeServiceInstanceMatch,
  getAuthThemeServiceInstanceId,
  useAuthTheme,
} from '@/lib/auth-theme'
import { isRootOrgServiceInstance } from '@/lib/org-hierarchy-from-instance'
import { FACILITYPRO_LOGO_SRC, FACILITYPRO_PRIMARY_COLOR, isFacilityProOrigin } from '@/lib/facilitypro-branding'

const isPendingRegistrationStatus = (value: unknown) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  return normalized === '1' || normalized === 'pending'
}

const INVALID_EMAIL_OR_MOBILE_PASSWORD_MESSAGE = 'Invalid Email or Mobile Number/Password.'
const ORGANIZATION_REFERENCE_LABEL = 'Business Code'
const ORGANIZATION_REFERENCE_REQUIRED_MESSAGE = 'Business code is required'
const ORGANIZATION_SIGNIN_UNAVAILABLE_MESSAGE = 'Organization not yet approved or currently suspended'

const lookupSchema = z.object({
  reference: z.string().trim().min(1, ORGANIZATION_REFERENCE_REQUIRED_MESSAGE),
})

const isEmailContact = (value: string) => value.includes('@')

const workspaceFinderSchema = z.object({
  contact: z.string().trim().min(1, 'Email or mobile number is required').refine(
    (value) => (isEmailContact(value)
      ? z.string().email().safeParse(value).success
      : value.replace(/\D/g, '').length >= 6),
    'Please enter a valid email address or mobile number',
  ),
})

type WorkspaceFinderFormData = z.infer<typeof workspaceFinderSchema>

/** Remembers the workspace last signed in to, so it is listed first next time. */
const LAST_LOGIN_WORKSPACE_KEY = 'login:last-workspace'

const readLastLoginWorkspace = () => {
  try {
    return window.localStorage.getItem(LAST_LOGIN_WORKSPACE_KEY) || ''
  } catch {
    return ''
  }
}

const rememberLoginWorkspace = (identifier: string) => {
  try {
    window.localStorage.setItem(LAST_LOGIN_WORKSPACE_KEY, identifier)
  } catch {
    // Storage blocked (private window): the picker just keeps server order.
  }
}


const resolveAllowedLoginMethods = (
  organizationDetails: Awaited<ReturnType<typeof orgTreeApi.resolvePublicSignupServiceInstance>> | null | undefined,
): Array<LoginFormData['loginMethod']> => {
  const emailAllowedByLoginConfig = organizationDetails ? organizationDetails.login_with_email !== false : true
  const mobileAllowedByLoginConfig = Boolean(organizationDetails?.login_with_mobile)
  const hasPublicSignupContactConfig = Boolean(
    organizationDetails?.signup_public_active
      && (organizationDetails.signup_public_email === true || organizationDetails.signup_public_mobile === true),
  )

  const emailAllowed = hasPublicSignupContactConfig
    ? emailAllowedByLoginConfig && organizationDetails?.signup_public_email === true
    : emailAllowedByLoginConfig
  const mobileAllowed = hasPublicSignupContactConfig
    ? mobileAllowedByLoginConfig && organizationDetails?.signup_public_mobile === true
    : mobileAllowedByLoginConfig

  if (emailAllowed && mobileAllowed) {
    return ['email', 'phn_nbr']
  }

  if (mobileAllowed) {
    return ['phn_nbr']
  }

  return ['email']
}

const resolveLoginErrorMessage = (error: unknown, loginMethod: 'email' | 'phn_nbr') => {
  const message = getErrorMessage(error)

  if (message.trim().toLowerCase() !== INVALID_EMAIL_OR_MOBILE_PASSWORD_MESSAGE.toLowerCase()) {
    return message
  }

  return loginMethod === 'phn_nbr'
    ? 'Invalid mobile number or password.'
    : 'Invalid email or password.'
}

const loginSchema = z.object({
  credential: z.string().trim().min(1, 'Email address is required'),
  loginMethod: z.enum(['email', 'phn_nbr']).default('email'),
  password: z.string().min(1, 'Password is required'),
  tenant: z.string().min(1, ORGANIZATION_REFERENCE_REQUIRED_MESSAGE),
}).superRefine((data, ctx) => {
  if (!data.tenant?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: ORGANIZATION_REFERENCE_REQUIRED_MESSAGE,
      path: ['tenant'],
    })
  }

  const credential = data.credential?.trim() ?? ''

  if (!credential) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: data.loginMethod === 'phn_nbr' ? 'Mobile number is required' : 'Email address is required',
      path: ['credential'],
    })
    return
  }

  if (data.loginMethod === 'email' && !z.string().email().safeParse(credential).success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please enter a valid email address',
      path: ['credential'],
    })
  }

  if (data.loginMethod === 'phn_nbr' && credential.replace(/\D/g, '').length < 6) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please enter a valid mobile number',
      path: ['credential'],
    })
  }
})

type LoginFormData = z.infer<typeof loginSchema>
type LookupFormData = z.infer<typeof lookupSchema>

/** Backend context: the root org (master admin) sign-in needs the emailed code. */
const LOGIN_OTP_REQUIRED_CONTEXT = 'login_otp_required'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const authenticatedUser = useAuthStore((state) => state.user)
  const serviceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const authTheme = useAuthTheme()
  const [authRedirectContext] = useState(() => consumeAuthRedirectContext())
  const [showPassword, setShowPassword] = useState(false)
  const [otpStep, setOtpStep] = useState<{ data: LoginFormData; maskedEmail: string; length: number } | null>(null)
  const [otpCode, setOtpCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResolvingOrganization, setIsResolvingOrganization] = useState(false)
  const [resolvedOrganizationOverride, setResolvedOrganizationOverride] = useState<Awaited<ReturnType<typeof orgTreeApi.resolvePublicSignupServiceInstance>> | null>(null)
  // No business code in the URL: ask for the email or mobile number, list its
  // workspaces, and picking one opens the usual password form for it -- unless
  // the user switches to typing the business code.
  const [useBusinessCode, setUseBusinessCode] = useState(false)
  const [workspaceChoices, setWorkspaceChoices] = useState<{ contact: string; workspaces: LoginWorkspace[] } | null>(null)
  const loginSearchParams = new URLSearchParams(location.search)
  const authRedirectReason = loginSearchParams.get('reason')
  const redirectFromQuery = loginSearchParams.get('from')
  const prefilledEmail = loginSearchParams.get('email') || ''
  const urlTenant = loginSearchParams.get('org')
    || loginSearchParams.get('id')
    || loginSearchParams.get('tenant')
    || loginSearchParams.get('company_identifier')
    || loginSearchParams.get('service_instance_id')
    || ''
  const themeTenant = authTheme.locked ? getAuthThemeServiceInstanceId(authTheme) : ''
  const queryTenant = themeTenant || urlTenant
  const sessionExpired = authRedirectReason === 'session-expired' || authRedirectContext?.reason === 'session-expired'
  const redirectCandidate = typeof location.state === 'object' && location.state && 'from' in location.state && typeof location.state.from === 'string'
    ? location.state.from
    : redirectFromQuery || authRedirectContext?.from
  const defaultHomePath = buildServiceVariantHomePath(currentPost, serviceInstanceId)
  const redirectTo = getSafeAuthRedirectTarget(redirectCandidate, serviceInstanceId)
    || defaultHomePath
  const isPendingAuthenticatedUser = isPendingRegistrationStatus(authenticatedUser?.registration_status)
  const shouldSelectPostFirst = isAuthenticated && availablePosts.length > 0 && !currentPost

  const {
    register: registerLookup,
    handleSubmit: handleLookupSubmit,
    setValue: setLookupValue,
    formState: { errors: lookupErrors },
  } = useForm<LookupFormData>({
    resolver: zodResolver(lookupSchema),
    defaultValues: {
      reference: queryTenant,
    },
  })

  const {
    register: registerFinder,
    handleSubmit: handleFinderSubmit,
    formState: { errors: finderErrors },
  } = useForm<WorkspaceFinderFormData>({
    resolver: zodResolver(workspaceFinderSchema),
    defaultValues: {
      contact: prefilledEmail,
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      tenant: queryTenant,
      loginMethod: 'email',
      credential: prefilledEmail,
    },
  })

  const tenantInput = watch('tenant')?.trim() ?? ''
  const selectedLoginMethod = watch('loginMethod')

  const organizationQuery = useQuery({
    queryKey: ['login-service-instance', selectedBackendServerUrl, queryTenant],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(queryTenant),
    enabled: queryTenant.length >= 3,
    staleTime: 60_000,
    retry: false,
    placeholderData: (previousData) => previousData,
  })

  const themedOrganization = authTheme.locked ? authTheme.organization : null
  // The query keeps the last company as placeholder data; without a business
  // code in the URL there is no company to show.
  const organizationDetails = themedOrganization ?? resolvedOrganizationOverride ?? (queryTenant ? organizationQuery.data : null)
  const organizationName = organizationDetails?.name
    || organizationDetails?.title
    || organizationDetails?.identifier
    || organizationDetails?.url_identifier
    || 'Ellan One'
  const { sidePanelLogos: resolvedSidePanelLogos, welcomeLogos: resolvedWelcomeLogos } = resolveServiceInstanceAuthLogos(organizationDetails)
  const termsText = organizationDetails?.terms_and_conditions ?? null
  const privacyText = organizationDetails?.privacy_policy ?? null
  const facilityProOrigin = isFacilityProOrigin(organizationDetails)
  const primaryColor = facilityProOrigin
    ? FACILITYPRO_PRIMARY_COLOR
    : organizationDetails?.primary_color || authTheme.primaryColor
  const sidePanelLogos = facilityProOrigin ? [FACILITYPRO_LOGO_SRC] : resolvedSidePanelLogos
  const welcomeLogos = facilityProOrigin ? [FACILITYPRO_LOGO_SRC] : resolvedWelcomeLogos
  const authTitle = facilityProOrigin ? 'FacilityPro' : organizationName
  const resolvedSignupServiceInstanceId = organizationDetails?.instance_id || String(organizationDetails?.id ?? '').trim()
  const signupHref = resolvedSignupServiceInstanceId
    ? `/signup/${encodeURIComponent(resolvedSignupServiceInstanceId)}`
    : '/signup'
  const canShowOrganizationSignup = isRootOrgServiceInstance(organizationDetails) || (!authTheme.locked && !queryTenant)
  const forgotPasswordTenant = tenantInput || queryTenant
  const forgotPasswordHref = forgotPasswordTenant
    ? `/forgot-password?tenant=${encodeURIComponent(forgotPasswordTenant)}`
    : '/forgot-password'
  const loginMethods = resolveAllowedLoginMethods(organizationDetails)
  const loginDisabled = organizationDetails?.login_enabled === false
  const hasInvalidOrganizationParam = Boolean(queryTenant)
    && !organizationQuery.isLoading
    && !organizationDetails
  const showOrganizationLookupStep = authTheme.locked ? false : (!queryTenant || hasInvalidOrganizationParam)
  const credentialLabel = selectedLoginMethod === 'phn_nbr' ? 'Mobile number' : 'Email address'
  const showMethodToggle = loginMethods.length > 1

  useEffect(() => {
    if (isAuthenticated) {
      navigate(
        shouldSelectPostFirst
          ? buildPostSelectionPath(serviceInstanceId, redirectCandidate)
          : isPendingAuthenticatedUser
            ? buildPostSelectionPath(serviceInstanceId, redirectCandidate)
            : redirectTo,
        { replace: true }
      )
    }
  }, [isAuthenticated, isPendingAuthenticatedUser, navigate, redirectCandidate, redirectTo, serviceInstanceId, shouldSelectPostFirst])

  useEffect(() => {
    if (sessionExpired) {
      toast.error('Your session expired. Sign in again to continue.')
    }
  }, [sessionExpired])

  useEffect(() => {
    const preferredMethod = loginMethods[0]

    if (!loginMethods.includes(selectedLoginMethod)) {
      setValue('loginMethod', preferredMethod, { shouldValidate: true })
    }
  }, [loginMethods, selectedLoginMethod, setValue])

  useEffect(() => {
    setValue('tenant', queryTenant, { shouldValidate: false, shouldDirty: false })
    setLookupValue('reference', queryTenant, { shouldValidate: false, shouldDirty: false })
  }, [queryTenant, setLookupValue, setValue])

  useEffect(() => {
    if (!prefilledEmail) {
      return
    }

    setValue('loginMethod', 'email', { shouldValidate: false, shouldDirty: false })
    setValue('credential', prefilledEmail, { shouldValidate: false, shouldDirty: false })
  }, [prefilledEmail, setValue])

  useEffect(() => {
    if (!queryTenant) {
      setResolvedOrganizationOverride(null)
      return
    }

    if (organizationQuery.data) {
      setResolvedOrganizationOverride((current) => {
        const currentInstanceId = String(current?.instance_id ?? current?.id ?? '').trim()
        const nextInstanceId = String(organizationQuery.data?.instance_id ?? organizationQuery.data?.id ?? '').trim()
        if (!current || currentInstanceId === nextInstanceId) {
          return organizationQuery.data
        }

        return current
      })
    }
  }, [organizationQuery.data, queryTenant])

  const clearOrganizationSelection = () => {
    if (authTheme.locked) {
      toast.info('This domain is locked to its configured organization.')
      return
    }

    setResolvedOrganizationOverride(null)
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('tenant')
    nextParams.delete('org')
    nextParams.delete('id')
    nextParams.delete('company_identifier')
    nextParams.delete('service_instance_id')
    navigate(`/login${nextParams.toString() ? `?${nextParams.toString()}` : ''}`, { replace: true })
  }

  const onLookupSubmit = async ({ reference }: LookupFormData) => {
    setIsResolvingOrganization(true)
    try {
      const instance = await orgTreeApi.resolvePublicSignupServiceInstance(reference.trim())
      if (!instance) {
        toast.error(ORGANIZATION_SIGNIN_UNAVAILABLE_MESSAGE)
        return null
      }

      setResolvedOrganizationOverride(instance)
      // identifier is the human-readable Business Code login actually accepts
      // as `tenant`; instance_id is the internal system ID (e.g.
      // "102-923226901378") -- falling back to it here silently breaks login.
      const resolvedReference = instance.identifier || instance.instance_id || String(instance.id)
      const nextParams = new URLSearchParams(location.search)
      nextParams.set('tenant', resolvedReference)
      nextParams.delete('org')
      nextParams.delete('id')
      nextParams.delete('company_identifier')
      nextParams.delete('service_instance_id')
      navigate(`/login?${nextParams.toString()}`, { replace: true })
      return instance
    } catch {
      toast.error('Unable to load organization details right now.')
      return null
    } finally {
      setIsResolvingOrganization(false)
    }
  }

  const onSubmit = (data: LoginFormData) => submitLogin(data)

  // Picking a workspace is the same as typing its business code: the usual
  // password form opens for it (forgot password, sign-in code and mobile
  // login included), with the email or number already filled in.
  const pickWorkspace = async (contact: string, workspace: LoginWorkspace) => {
    rememberLoginWorkspace(workspace.identifier)
    const instance = await onLookupSubmit({ reference: workspace.identifier })
    if (!instance) {
      return
    }
    const loginMethod = isEmailContact(contact) ? 'email' : 'phn_nbr'
    if (!resolveAllowedLoginMethods(instance).includes(loginMethod)) {
      toast.info(loginMethod === 'email'
        ? 'This workspace signs in with a mobile number.'
        : 'This workspace signs in with an email address.')
      return
    }
    setValue('loginMethod', loginMethod, { shouldValidate: false })
    setValue('credential', contact, { shouldValidate: false })
  }

  const onFinderSubmit = async ({ contact }: WorkspaceFinderFormData) => {
    const trimmedContact = contact.trim()
    setIsLoading(true)
    let workspaces: LoginWorkspace[]
    try {
      workspaces = await authApi.findLoginWorkspaces(isEmailContact(trimmedContact)
        ? { email: trimmedContact }
        : { phn_nbr: trimmedContact.replace(/\D/g, '') })
    } catch (error) {
      toast.error(getErrorMessage(error))
      setIsLoading(false)
      return
    }
    setIsLoading(false)

    if (workspaces.length === 0) {
      toast.error('No workspace found for that email or mobile number. Check it, or sign in with your business code.')
      return
    }
    if (workspaces.length === 1) {
      setWorkspaceChoices(null)
      void pickWorkspace(trimmedContact, workspaces[0])
      return
    }
    const lastWorkspace = readLastLoginWorkspace()
    setWorkspaceChoices({
      contact: trimmedContact,
      workspaces: [...workspaces].sort((a, b) => Number(b.identifier === lastWorkspace) - Number(a.identifier === lastWorkspace)),
    })
  }

  const onOtpSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (otpStep && otpCode.trim()) {
      void submitLogin(otpStep.data, otpCode.trim())
    }
  }

  const submitLogin = async (data: LoginFormData, loginOtp?: string) => {
    setIsLoading(true)
    try {
      const credential = data.credential.trim()
      const tenantReference = data.tenant.trim()
      const loginServiceInstanceId = resolvedSignupServiceInstanceId || tenantReference
      const response = await authApi.login({
        tenant: tenantReference,
        service_instance_id: loginServiceInstanceId || undefined,
        password: data.password,
        type: data.loginMethod,
        ...(loginOtp ? { login_otp: loginOtp } : {}),
        ...(data.loginMethod === 'phn_nbr'
          ? { phn_nbr: credential }
          : { email: credential }),
      })

      // Check for special contexts that require redirection
      if (response.data?.context) {
        const context = response.data.context
        // Root-org (master admin) sign-in: a code was emailed; ask for it and send the login again.
        if (context === LOGIN_OTP_REQUIRED_CONTEXT) {
          setOtpStep({
            data,
            maskedEmail: String(response.data.masked_email ?? ''),
            length: Number(response.data.otp_length) || 6,
          })
          setOtpCode('')
          toast.info(response.message || 'Enter the sign-in code we emailed you.')
          return
        }
        if (context.includes('reset-password') || context.includes('reset_password')) {
          const query = context.includes('?') ? context.split('?')[1] : ''
          const resetParams = new URLSearchParams(query)
          if (!resetParams.get('credential')) {
            resetParams.set('credential', credential)
          }
          if (!resetParams.get('login_method')) {
            resetParams.set('login_method', data.loginMethod)
          }
          const token = resetParams.get('token') || ''
          const finalQuery = resetParams.toString()
          toast.info('Password reset required')
          navigate(token ? `/reset-password?${finalQuery}` : '/reset-password')
          return
        }
        if (isSignupEmailOtpVerificationContext(context) && data.loginMethod === 'email') {
          navigate(buildSignupEmailVerificationPath({
            email: credential,
            tenant: tenantReference,
            orgInstanceId: response.data.service_instance_id || tenantReference,
            otpLength: response.data.otp_length,
          }))
          return
        }
        if (context.includes('verification') || context.includes('signed-up-verification-link-sent-to-email')) {
          const maskedTarget = typeof response.data.message === 'string' ? response.data.message : ''
          const verificationMessage = typeof response.more_info?.message === 'string'
            ? response.more_info.message
            : response.message

          toast.info(maskedTarget ? `${verificationMessage} ${maskedTarget}` : verificationMessage)
          return
        }
      }

      if (!response.data?.access_token) {
        toast.info(response.message || 'Additional sign-in step required')
        return
      }

      if (
        authTheme.locked
        && response.data.service_instance_id
        && !assertAuthThemeServiceInstanceMatch(authTheme, response.data.service_instance_id)
      ) {
        toast.error('This sign-in response does not match the organization configured for this domain.')
        return
      }

      if (response.data.billing?.is_locked) {
        navigate('/billing/locked', { replace: true })
        return
      }

      toast.success('Welcome back!')
      const currentServiceInstanceId = useAuthStore.getState().serviceInstanceId
      const pendingUser = isPendingRegistrationStatus(response.data?.user?.registration_status)
      const hasAssignablePosts = (response.data?.posts ?? []).length > 0
      if (pendingUser) {
        toast.info('Your account is still pending approval.')
      }
      navigate(
        pendingUser || hasAssignablePosts
          ? buildPostSelectionPath(
              currentServiceInstanceId,
              redirectTo === defaultHomePath
                ? null
                : redirectTo,
            )
          : withServiceInstanceId('/profile', currentServiceInstanceId || serviceInstanceId || null),
        { replace: true }
      )
    } catch (error) {
      toast.error(resolveLoginErrorMessage(error, data.loginMethod))
    } finally {
      setIsLoading(false)
    }
  }

  const otpForm = otpStep ? (
    <form onSubmit={onOtpSubmit} className="mx-auto max-w-md space-y-3">
      <p className="text-center text-sm text-muted-foreground">
        We emailed a sign-in code to <span className="font-semibold text-foreground">{otpStep.maskedEmail || 'your email'}</span>.
        It expires in 10 minutes.
      </p>
      <div className="space-y-2">
        <Label htmlFor="login-otp" className="sr-only">Sign-in code</Label>
        <Input
          id="login-otp"
          className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center text-lg tracking-[0.4em] shadow-none placeholder:tracking-normal placeholder:text-primary/45 focus-visible:ring-primary/35"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={`${otpStep.length}-digit code`}
          maxLength={otpStep.length}
          value={otpCode}
          onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, ''))}
          disabled={isLoading}
          autoFocus
        />
      </div>
      <Button
        type="submit"
        className="mx-auto flex h-11 min-w-44 rounded-full px-8"
        disabled={isLoading || otpCode.length !== otpStep.length}
      >
        {isLoading && <Spinner className="mr-2" />}
        Verify and sign in
      </Button>
      <div className="flex justify-center gap-4 text-xs font-semibold text-muted-foreground">
        <button type="button" className="hover:text-primary hover:underline" disabled={isLoading} onClick={() => void submitLogin(otpStep.data)}>
          Send a new code
        </button>
        <button type="button" className="hover:text-primary hover:underline" disabled={isLoading} onClick={() => { setOtpStep(null); setOtpCode('') }}>
          Back
        </button>
      </div>
    </form>
  ) : null

  return (
    <TenantAuthShell
      organizationName={organizationName}
      sidePanelLogos={sidePanelLogos}
      welcomeLogos={welcomeLogos}
      primaryColor={primaryColor}
      icon={Briefcase}
      rightLogoContainerClassName="relative z-10 mb-4 mt-0 flex w-full flex-col items-center justify-center text-center"
      eyebrow=""
      title={authTitle}
      description=""
      panelTitle=""
      panelDescription=""
      logoPosition="left"
      contentVerticalAlign="center"
      contentPanelClassName="rounded-[24px] bg-white/95 px-5 py-5 text-foreground shadow-xl shadow-slate-950/15 backdrop-blur sm:px-6 sm:py-6"
      sideActions={(
        <div className="space-y-3 text-sm">
          <p className="text-primary-foreground/80">Don&apos;t have an account? Choose what you&apos;re setting up:</p>
          <div className="flex flex-col gap-2">
            <Link to={signupHref} className="flex items-start gap-3 rounded-2xl bg-white/95 px-4 py-2.5 text-left shadow-sm transition hover:bg-white">
              <UserCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                <span className="block font-semibold text-primary">Join a team</span>
                <span className="block text-xs text-muted-foreground">I have my company&apos;s business code and I&apos;m joining their account</span>
              </span>
            </Link>
            {canShowOrganizationSignup ? (
              <Link to="/signup/services-company" className="flex items-start gap-3 rounded-2xl bg-white/15 px-4 py-2.5 text-left ring-1 ring-white/30 transition hover:bg-white/25">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-white" />
                <span>
                  <span className="block font-semibold text-white">Start a security company</span>
                  <span className="block text-xs text-white/70">I&apos;m setting up guarding, shifts, and site coverage</span>
                </span>
              </Link>
            ) : null}
            {canShowOrganizationSignup ? (
              <Link to="/signup/company" className="flex items-start gap-3 rounded-2xl bg-white/15 px-4 py-2.5 text-left ring-1 ring-white/30 transition hover:bg-white/25">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-white" />
                <span>
                  <span className="block font-semibold text-white">Other organization</span>
                  <span className="block text-xs text-white/70">I have a specific parent organization reference to sign up under</span>
                </span>
              </Link>
            ) : null}
          </div>
        </div>
      )}
    >
          <Card className="border-0 bg-transparent shadow-none">
            <CardContent className="px-0">
              {sessionExpired ? (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Your previous session is no longer valid. Sign in again to reopen the page you were using.
                </div>
              ) : null}
              {organizationQuery.isFetching && !organizationDetails ? (
                <div className="mb-4 rounded-2xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                  Resolving organization details...
                </div>
              ) : null}
              {organizationDetails?.login_otp_authentication ? (
                <div className="mb-4 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">
                  This organization requires OTP verification during sign-in.
                </div>
              ) : null}
              {loginDisabled ? (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Sign-in is currently disabled for this organization.
                </div>
              ) : null}
              {hasInvalidOrganizationParam ? (
                <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  {ORGANIZATION_SIGNIN_UNAVAILABLE_MESSAGE}
                </div>
              ) : null}

              {showOrganizationLookupStep ? (
                otpForm ?? (workspaceChoices ? (
                  <div className="mx-auto max-w-md space-y-3">
                    <p className="text-center text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{workspaceChoices.contact}</span> belongs to more than one workspace. Choose the one to sign in to.
                    </p>
                    <div className="space-y-2">
                      {workspaceChoices.workspaces.map((workspace) => (
                        <button
                          key={workspace.instance_id}
                          type="button"
                          disabled={isResolvingOrganization}
                          onClick={() => void pickWorkspace(workspaceChoices.contact, workspace)}
                          className="flex w-full items-center gap-3 rounded-2xl bg-primary/10 px-4 py-3 text-left transition hover:bg-primary/20 disabled:opacity-60"
                        >
                          <Building2 className="h-4 w-4 shrink-0 text-primary" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold text-foreground">{workspace.name}</span>
                            <span className="block text-xs text-muted-foreground">
                              {[workspace.product, workspace.identifier].filter(Boolean).join(' · ')}
                            </span>
                          </span>
                          {isResolvingOrganization ? <Spinner /> : <ChevronRight className="h-4 w-4 shrink-0 text-primary/60" />}
                        </button>
                      ))}
                    </div>
                    <div className="text-center">
                      <button type="button" disabled={isResolvingOrganization} onClick={() => setWorkspaceChoices(null)} className="text-xs font-semibold text-muted-foreground hover:text-primary hover:underline">
                        Back
                      </button>
                    </div>
                  </div>
                ) : useBusinessCode ? (
                <form onSubmit={handleLookupSubmit(onLookupSubmit)} className="mx-auto max-w-md space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="reference" className="sr-only">{ORGANIZATION_REFERENCE_LABEL}</Label>
                    <Input
                      id="reference"
                      className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                      placeholder="Business Code (e.g. ABCDEF)"
                      autoComplete="off"
                      spellCheck={false}
                      disabled={isResolvingOrganization}
                      {...registerLookup('reference')}
                      aria-invalid={!!lookupErrors.reference}
                    />
                    {lookupErrors.reference ? (
                      <p className="text-center text-sm text-destructive">{lookupErrors.reference.message}</p>
                    ) : null}
                  </div>

                  <Button type="submit" className="mx-auto flex h-11 min-w-44 rounded-full px-8" disabled={isResolvingOrganization}>
                    {isResolvingOrganization ? <Spinner className="mr-2" /> : <Search className="mr-2 h-4 w-4" />}
                    Continue
                  </Button>
                  <div className="text-center">
                    <button type="button" onClick={() => setUseBusinessCode(false)} className="text-xs font-semibold text-muted-foreground hover:text-primary hover:underline">
                      Sign in with your email instead
                    </button>
                  </div>
                </form>
                ) : (
                <form onSubmit={handleFinderSubmit(onFinderSubmit)} className="mx-auto max-w-md space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="finder-contact" className="sr-only">Email or mobile number</Label>
                    <Input
                      id="finder-contact"
                      className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                      placeholder="Email or mobile number"
                      autoComplete="username"
                      disabled={isLoading || isResolvingOrganization}
                      {...registerFinder('contact')}
                      aria-invalid={!!finderErrors.contact}
                    />
                    {finderErrors.contact ? (
                      <p className="text-center text-sm text-destructive">{finderErrors.contact.message}</p>
                    ) : null}
                  </div>

                  <Button type="submit" className="mx-auto flex h-11 min-w-44 rounded-full px-8" disabled={isLoading || isResolvingOrganization}>
                    {(isLoading || isResolvingOrganization) && <Spinner className="mr-2" />}
                    Continue
                  </Button>
                  <div className="text-center">
                    <button type="button" onClick={() => setUseBusinessCode(true)} className="text-xs font-semibold text-muted-foreground hover:text-primary hover:underline">
                      Sign in with a business code instead
                    </button>
                  </div>
                </form>
                ))
              ) : (
                <>
                  <div className="mb-5 rounded-2xl bg-primary/10 px-4 py-3 text-center text-sm text-muted-foreground">
                    Signing in to <span className="font-semibold text-foreground">{organizationName}</span>.
                    <button type="button" onClick={clearOrganizationSelection} className="ml-2 font-semibold text-primary hover:underline">
                      Change
                    </button>
                  </div>
                  {otpForm ?? (
                  <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-md space-y-3">
                    <input type="hidden" {...register('tenant')} />

                    {showMethodToggle ? (
                      <div className="grid grid-cols-2 gap-2 rounded-full bg-primary/10 p-1">
                        <Button
                          type="button"
                          className="rounded-full"
                          variant={selectedLoginMethod === 'email' ? 'default' : 'ghost'}
                          onClick={() => setValue('loginMethod', 'email', { shouldValidate: true })}
                          disabled={isLoading || loginDisabled}
                        >
                          Email
                        </Button>
                        <Button
                          type="button"
                          className="rounded-full"
                          variant={selectedLoginMethod === 'phn_nbr' ? 'default' : 'ghost'}
                          onClick={() => setValue('loginMethod', 'phn_nbr', { shouldValidate: true })}
                          disabled={isLoading || loginDisabled}
                        >
                          Mobile
                        </Button>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <Label htmlFor="credential" className="sr-only">{credentialLabel}</Label>
                      <Input
                        id="credential"
                        className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                        type={selectedLoginMethod === 'phn_nbr' ? 'tel' : 'email'}
                        placeholder={credentialLabel}
                        autoComplete={selectedLoginMethod === 'phn_nbr' ? 'tel' : 'email'}
                        disabled={isLoading || loginDisabled}
                        {...register('credential')}
                        aria-invalid={!!errors.credential}
                      />
                      {errors.credential && (
                        <p className="text-center text-sm text-destructive">{errors.credential.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="relative">
                        <Label htmlFor="password" className="sr-only">Password</Label>
                        <Input
                          id="password"
                          className="h-11 rounded-full border-0 bg-primary/15 px-5 pr-12 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          autoComplete="current-password"
                          disabled={isLoading || loginDisabled}
                          {...register('password')}
                          aria-invalid={!!errors.password}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/60 hover:text-primary"
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-center text-sm text-destructive">{errors.password.message}</p>
                      )}
                    </div>

                    <div className="text-center">
                      <Link to={forgotPasswordHref} className="text-xs font-semibold text-muted-foreground hover:text-primary hover:underline">
                        Forgot your password?
                      </Link>
                    </div>

                    <Button type="submit" className="mx-auto flex h-11 min-w-44 rounded-full px-8" disabled={isLoading || loginDisabled}>
                      {isLoading && <Spinner className="mr-2" />}
                      Log in
                    </Button>
                  </form>
                  )}
                </>
              )}

              <AuthTermsDisclosure
                termsText={termsText}
                privacyText={privacyText}
                className="mt-4 text-center"
              />
            </CardContent>
          </Card>
    </TenantAuthShell>
  )
}
